import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject } from 'rxjs';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { User } from 'src/app/user/model/user';
import { UserService } from '../service/user.service';
import { EventEmitter, Output } from '@angular/core';
import { AppConfigService, AppConfiguration } from '../service/app-config.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { WebSocketService } from '../service/web-socket.service';
import { Router } from '@angular/router';
import { Authenticate } from '../model/authenticate';
import { isEmpty, sortBy } from 'lodash';
import { SettingService } from '../service/setting.service';
import { SelectRoleComponent } from 'src/app/shared/select-role/select-role.component';
import { FHIR_DEFAULT_PAGE_URL, FHIR_DEFAULT_PAGE_URL_FULL_SITE, FHIR_NAVIGATE_TO_PATIENT_SUMMARY, FhirService } from '../service/fhir.service';
import { UserPasswordExpiration } from '../model/userPasswordExpiration';
import { ChangePasswordComponent } from 'src/app/shared/change-password/change-password.component';
import { SelectOrganizationComponent } from 'src/app/shared/select-organization/select-organization.component';
import { IdleService } from '../service/idle.service';
import { jwtDecode } from 'jwt-decode';
import { clearTabSessionHeartbeat, updateHeartbeat } from '../service/browser-tab-session-cleanup';
import { BrowserTabSessionService } from '../service/browser-tab-session.service';

/**
 * Provides authentication service.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService
{
  @Output() public authenticationChange = new EventEmitter<User>();

  public accessReports = false;
  public accessMessages = false;
  public activeRoles: Array<{ text: string; value: string }> = [];

  private isLoggedInSubject = new BehaviorSubject<boolean>(this.currentUser !== null);
  private appConfig: AppConfiguration;
  private cacheTokenToLocalStorage = false;
  private verificationTwoFaStorageKey = 'verificationTwoFa';
  private completingLogin = false;

  constructor(
    private http: HttpClient,
    private settingService: SettingService,
    private logger: NGXLogger,
    private permissionsService: NgxPermissionsService,
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private userService: UserService,
    private appConfigService: AppConfigService,
    private webSocketService: WebSocketService,
    private router: Router,
    private fhirService: FhirService,
    private injector: Injector)
  {
    this.appConfig = this.appConfigService.getConfig();
    this.initTokenStorage();
  }

  /**
   * The current logged-in user.
   *
   * @returns User Current logged in user. Returns null if no user is logged in.
   */
  public get currentUser(): User
  {
    const currentUser: User = this.cacheTokenToLocalStorage ?
      this.localStorageService.retrieve('user') : this.sessionStorageService.retrieve('user');

    if (currentUser && currentUser.token)
    {
      if (isEmpty(this.permissionsService.getPermissions()))
      {
        // This logic could be on login, but putting it here allows it to survive a browser refresh.
        this.loadPermissions(currentUser);
        this.webSocketService.connect(currentUser.token);  // Connect WebSocket
        // If we are in a change password process, we don't want to login even if the user is persisted in the storage
        if (currentUser.changePassword !== undefined)
        {
          setTimeout(() =>
          {
            this.isLoggedInSubject.next(true);
            this.authenticationChange.next(currentUser);
          }, 500);
        }
      }
      return currentUser;
    }
    else
    {
      if (this.isLoggedInSubject !== undefined)
      {
        this.isLoggedInSubject.next(false);
      }
      return null;
    }
  }

  /**
   * Indicates if the user is authenticated.
   *
   * @returns boolean
   */
  public get isAuthenticated(): boolean
  {
    const user = this.currentUser;
    if (user && user.token && !this.isTokenExpired(user.token))
    {
      return true;
    }
    else
    {
      return false;
    }
  }

  /** True while the user is on the post-auth role/organization selection step. */
  public get isCompletingLogin(): boolean
  {
    return this.completingLogin;
  }

  /**
   * Return the temporary token for two factor (totp) code verification only.
   */
  public get totpToken(): string
  {
    return this.sessionStorageService.retrieve(this.verificationTwoFaStorageKey)?.token || null;
  }

  public get totpQRCode(): any
  {
    return this.sessionStorageService.retrieve(this.verificationTwoFaStorageKey)?.qrCode || null;
  }

  public get totpUserName(): string
  {
    return this.sessionStorageService.retrieve(this.verificationTwoFaStorageKey)?.userName || null;
  }

  public get totpUrlParams(): string
  {
    return this.sessionStorageService.retrieve(this.verificationTwoFaStorageKey)?.urlParams || null;
  }

  public get totpReturnUrl(): string
  {
    return this.sessionStorageService.retrieve(this.verificationTwoFaStorageKey)?.returnUrl || null;
  }

  public get totpMobile(): string
  {
    return this.sessionStorageService.retrieve(this.verificationTwoFaStorageKey)?.mobile || null;
  }

  public get totpEmail(): string
  {
    return this.sessionStorageService.retrieve(this.verificationTwoFaStorageKey)?.email || null;
  }

  public get totpDifferentAuthMethodAllowed(): boolean
  {
    return this.sessionStorageService.retrieve(this.verificationTwoFaStorageKey)?.differentAuthMethodAllowed || false;
  }

  public get totFHIR(): boolean
  {
    return this.sessionStorageService.retrieve(this.verificationTwoFaStorageKey)?.FHIR || false;
  }

  /**
   * Indicates if a JWT bearer token is expired or cannot be decoded.
   *
   * @param token JWT bearer token
   * @returns boolean
   */
  public isTokenExpired(token: string): boolean
  {
    try
    {
      const decoded = jwtDecode<{ exp?: number }>(token);
      if (!decoded.exp)
      {
        return false;
      }
      return decoded.exp * 1000 <= Date.now();
    }
    catch
    {
      return true;
    }
  }

  /**
   * Verify two factor authorization code.
   */
  public verifyCode(verify: any): Observable<any>
  {
    return this.http.post<any>(`/authenticate/code/verify`, verify);
  }

  public sendCode(send: any): Observable<any>
  {
    return this.http.post<any>(`/authenticate/code/send`, send);
  }

  /**
   * Indicates if the user is logged in.
   *
   * @returns Observable<boolean>
   */
  public isLoggedIn(): Observable<boolean>
  {
    return this.isLoggedInSubject.asObservable();
  }

  /**
   * Login the user then tell all the subscribers about the new status
   *
   * @param username Login username
   * @param password Login password
   * @returns Observable<object> HTTP observable response.
   */
  public authenticate(username: string, password: string, selectedLanguage: string): Observable<any>
  {
    this.logger.info('authenticate');
    const credentials = new Authenticate();
    credentials.username = username;
    credentials.password = password;
    credentials.selectedLanguage = selectedLanguage;
    credentials.fhirIssuerId = this.fhirService?.issuer ? this.fhirService.issuer.id : null;
    credentials.fhirProfileId = this.fhirService?.oAuthToken?.user ? decodeURIComponent(this.fhirService.oAuthToken.user) : null;
    credentials.fhirIdToken = this.fhirService?.oAuthToken?.id_token ? this.fhirService?.oAuthToken?.id_token : null;
    return this.http.post('/authenticate', credentials);
  }

  /**
   * Logs in to Flowable IDM.
   *
   * @param username Current logged-in UserName
   * @param password Current logged-in password
   * @returns Observable<any> HTTP observable response.
   */
  public authenticateFlowable(username: string, password: string): Observable<any>
  {
    const credentials: FormData = new FormData();
    credentials.append('j_username', username);
    credentials.append('j_password', password);
    credentials.append('_spring_security_remember_me', 'true');
    credentials.append('submit', 'Login');

    // Send authentication request to Flowable's IDM API.
    return this.http.post(`${this.appConfig.flowable.baseUrl}/flowable-idm/app/authentication`, credentials, { withCredentials: true });
  }

  /**
   * Logs out of Flowable IDM.
   *
   * @returns Observable<any> HTTP observable response.
   */
  public logoutFlowable(): Observable<any>
  {
    return this.http.post(`${this.appConfig.flowable.baseUrl}/flowable-idm/app/logout`, null);
  }

  public logoutChannels(): Observable<any>
  {
    return this.http.post(`/logout`, null);
  }

  /**
   * Log in the user for the successful authentication. It save the current user to storage, set isLoginSubject and navigate.
   *
   * @param user Authenticate response object from the server.
   * @param returnUrlArg Returned url if used.
   * @param selectRole Selected Role Component
   * @param selectOrganization Selected Organization Component
   * @param changePassword Change User Password Component
   * @param [isFHIR=false] When true, it is a FHIR authentication using SSO. For FHIR, we skip two step authentication if set.
   */
  public login(user: User, returnUrlArg: string, selectRole: SelectRoleComponent,
    selectOrganization: SelectOrganizationComponent,
    changePassword: ChangePasswordComponent,
    isFHIR = false): void
  {
    // If twoFa and not authenticated, we must validate 6 digits code
    if (user.twoFa && !user.authenticated && !isFHIR)
    {
      // Store temporary token
      this.sessionStorageService.store(this.verificationTwoFaStorageKey,
        {
          token: user.token,
          qrCode: user.qrCode,
          userName: user.userName,
          returnUrl: returnUrlArg,
          email: user.email,
          mobile: user.mobile,
          differentAuthMethodAllowed: user.differentAuthMethodAllowed,
          FHIR: isFHIR
        });
      this.router.navigate(['totp']);
    }
    else
    {
      // No two factor authentication enabled therefore complete the login process.
      this.completeLoginProcess(user, returnUrlArg, selectRole, selectOrganization, changePassword, isFHIR);
    }
  }

  /**
   * This method complete the login process after validating the 6 digits code if two fa enabled.
   *
   * @param user Current user object returned from the authentication
   * @param returnUrl Returned URL is any
   * @param selectRole Select Role component
   * @param selectOrganization Select Organization component
   * @param changePassword Change User Password Component
   * @param isFHIR True if FHIR context
   */
  public completeLoginProcess(user: any, returnUrl: string,
    selectRole: SelectRoleComponent,
    selectOrganization: SelectOrganizationComponent,
    changePassword: ChangePasswordComponent,
    isFHIR: boolean): void
  {
    this.completingLogin = true;
    this.sessionStorageService.clear(this.verificationTwoFaStorageKey);
    this.saveUserToStorage(user);
    if (this.cacheTokenToLocalStorage)
    {
      updateHeartbeat();
    }
    this.injector.get(IdleService).startIdle();

    // Allow Role changes at login
    if (user.allowedRoles && user.allowedRoles.length > 1)
    {
      this.activeRoles = sortBy(this.userService.roles.filter(r => user.allowedRoles.includes(r.value)), ['text']);
      selectRole.execute(user.userName, user.role, this.activeRoles).then(() =>
      {
        // Allow Organization changes at login
        if (user.allowedOrganizations && user.allowedOrganizations.length > 1) 
        {
          selectOrganization.execute(user.userName, user.organizationId, user.allowedOrganizations).then(() =>
          {
            this.finalizeLoginProcess(user, returnUrl, changePassword, isFHIR, false);
          });
        }
        else
        {
          this.finalizeLoginProcess(user, returnUrl, changePassword, isFHIR, false);
        }
      });
    }
    else
    {
      // Allow Organization changes at login
      if (user.allowedOrganizations && user.allowedOrganizations.length > 1) 
      {
        selectOrganization.execute(user.userName, user.organizationId, user.allowedOrganizations).then(() =>
        {
          this.finalizeLoginProcess(user, returnUrl, changePassword, isFHIR, false);
        });
      }
      else
      {
        this.finalizeLoginProcess(user, returnUrl, changePassword, isFHIR, false);
      }
    }
  }

  /**
   * Change user role method is being used from the header to allow a logged in user to switch to any allowed roles.
   *
   * @param user Current logged in used.
   */
  public changeUserRole(user: any): void
  {
    // Should be authenticated to run this method. This is just a simple sanitity check
    if (this.isAuthenticated)
    {
      this.finalizeLoginProcess(user, null, null, false, true);
    }
  }

  /**
   * Navigate to default page based on user role.
   */
  public navigateToDefaultPage(returnUrl = '', isRoleChanges = false): void
  {
    // Navigate to URL
    setTimeout(() =>
    {
      const user = this.currentUser;
      if (user)
      {
        let url = returnUrl;

        if (this.fhirService.fullSite)
        {
          url = FHIR_DEFAULT_PAGE_URL_FULL_SITE;
        }
        else
          // If embedded FHIR, use the default url
          if (this.fhirService.embedded)
          {
            url = FHIR_DEFAULT_PAGE_URL;
          }

        // If no return url, then set a default
        if (url === '' || url === null)
        {
          if (user.role === 'ROLE_DATA_ANALYST')
          {
            this.settingService.getCachedSessionSettings().subscribe({
              next: (sessionSetting) =>
              {
                this.accessReports = (sessionSetting.reportRoles !== '') ?
                  this.userService.isRoleInList(user.role, sessionSetting.reportRoles) : false;

                this.accessMessages = (sessionSetting.messagesRoles !== '') ?
                  this.userService.isRoleInList(user.role, sessionSetting.messagesRoles) : false;

                url = '/help';    // Reports and messages is not accessible therefore navigate to help (always available)
                if (this.accessReports || this.accessMessages)
                {
                  url = this.accessReports ? 'reports' : 'messages/inbox';
                }
                this.router.navigateByUrl(`${url}`);
                if (isRoleChanges) { setTimeout(() => { window.location.reload() }); }
              }
            });
          }
          else
          {
            switch (user.role)
            {
              case 'ROLE_CARE_COORDINATOR':
                url = 'dashboards/all';
                break;
              case 'ROLE_PROGRAM_LEAD':
              case 'ROLE_PROGRAM_SUPPORT':
                url = 'cases/active';
                break;
              case 'ROLE_SYSTEM_OPERATOR':
                url = 'field-configurations';
                break;
              case 'ROLE_COMMUNITY_COORDINATOR':
                url = 'programs/all';
                break;
              case 'ROLE_COMMUNITY_ADMIN':
                url = 'users';
                break;
              case 'ROLE_REFERRAL_COORDINATOR':
                url = 'referral-tasks/my';
                break;
              case 'ROLE_DIRECTORY_MANAGER':
                url = 'settings';
                break;
              case 'ROLE_REVENUE_MANAGER_OPERATOR':
                url = 'payer-billing';
                break;
              case 'ROLE_REVENUE_MANAGER_ANALYST':
                url = 'payer-billing';
                break;
              case 'ROLE_CBO_REVENUE_ANALYST':
                url = 'cbo-payments';
                break;
              default:
                url = 'cases/active';
                break;
            }
          }
          this.router.navigateByUrl(`${url}`);
          if (isRoleChanges) { setTimeout(() => { window.location.reload() }); }
        }
        else
        {
          if (this.fhirService.fullSite)
          {
            this.sessionStorageService.store(FHIR_NAVIGATE_TO_PATIENT_SUMMARY, true);
            this.router.navigate([FHIR_DEFAULT_PAGE_URL_FULL_SITE]);
          }
          else
          {
            this.router.navigateByUrl(`${url}`);
          }
        }
      }
    });
  }

  /**
   * Logout the current user by removing the session storage and navigate to login page.
   */
  public logout(): void
  {
    this.reset(true);
    this.router.navigate(['login']);
  }

  /**
   * Reset the session to ensure starting with clean session (equivalent to logout except no login navigation).
   *
   * @param isLogout When true, calls server logout endpoints and clears FHIR storage.
   * @param broadcastToOtherTabs When true, notifies other open tabs to reset (multi-tab localStorage mode).
   * @param force When true, clears the session even during role/organization selection at login.
   */
  public reset(isLogout = false, broadcastToOtherTabs = true, force = false): void
  {
    if (this.completingLogin && !isLogout && !force)
    {
      return;
    }

    this.completingLogin = false;

    if (this.currentUser && this.currentUser.token) 
    {
      this.sessionStorageService.store('logout_token', this.currentUser.token);
    }

    if (broadcastToOtherTabs && this.cacheTokenToLocalStorage)
    {
      this.injector.get(BrowserTabSessionService).broadcastLogout();
    }

    if (isLogout)
    {
      this.fhirService.clearStorage(isLogout);
    }

    if (this.cacheTokenToLocalStorage)
    {
      this.localStorageService.clear('user');
      this.sessionStorageService.clear(this.verificationTwoFaStorageKey);
      clearTabSessionHeartbeat();
    }
    else
    {
      this.sessionStorageService.clear('user');
    }

    setTimeout(() =>
    {
      if (isLogout)
      {
        this.logoutFlowable().subscribe();
        if (this.sessionStorageService.retrieve('logout_token'))
        {
          this.logoutChannels().subscribe();
        }
      }
      this.webSocketService.disconnect();
      this.authenticationChange.next(null);
      this.isLoggedInSubject.next(false);
      this.permissionsService.flushPermissions();
      this.activeRoles = [];
    });
  }

  /**
   * Update the full username stored in the session storage.
   * This is being called when changing First or Last name from User Account.
   */
  public updateUsername(newUsername: string): void
  {
    if (newUsername && newUsername !== '')
    {
      let currentUser = null;
      if (this.cacheTokenToLocalStorage)
      {
        currentUser = this.localStorageService.retrieve('user');
      }
      else
      {
        currentUser = this.sessionStorageService.retrieve('user');
      }
      if (currentUser !== null)
      {
        currentUser.name = newUsername;
        this.saveUserToStorage(currentUser);
      }
    }
  }

  /**
   * Save the current user to either the localStorage or sessionStorage.
   *
   * @param user User object to persist to application storage.
   */
  public saveUserToStorage(user: any): void
  {
    this.initTokenStorage();
    if (this.cacheTokenToLocalStorage)
    {
      this.localStorageService.store('user', user);
    }
    else
    {
      this.sessionStorageService.store('user', user);
    }
  }

  private loadPermissions(currentUser: User): void
  {
    const perm = [];
    if (currentUser.role)
    {
      perm.push(currentUser.role);
    }
    this.permissionsService.loadPermissions(perm);
  }

  /**
   * Method called from completeLoginProcess to complete the final login steps
   *
   * @param user Current user object returned from the authentication
   * @param returnUrl Returned URL is any
   * @param changePassword Change User Password Component
   * @param isFHIR True if FHIR context 
   * @param isRoleChanges True if Roles change context (after login only)
   */
  private finalizeLoginProcess(user: any, returnUrl: string,
    changePassword: ChangePasswordComponent,
    isFHIR: boolean, isRoleChanges: boolean): void
  {
    this.userService.getUserByUsername(user.userName).subscribe({
      next: (userInfo) =>
      {
        // Check password changes
        if (!isFHIR && changePassword)  
        {
          this.userService.getUserPasswordExpiration().subscribe({
            next: (userPasswordExp: UserPasswordExpiration) =>
            {
              // If password is expired or x days before expiration, where x is passwordExpirationDaysWarning
              if (userPasswordExp.daysSince && userPasswordExp.passwordExpirationDays &&
                userPasswordExp.daysSince > 0 && userPasswordExp.passwordExpirationDays > 0 &&
                (userPasswordExp.daysSince + userPasswordExp.passwordExpirationDaysWarning) >= userPasswordExp.passwordExpirationDays) 
              {
                this.processLoginUser(user, userInfo, true);
                changePassword.execute(userPasswordExp).then((changePasswordResult) =>
                {
                  // If changePasswordResult is null, the password was not changed
                  if (!changePasswordResult)
                  {
                    this.logout();
                    window.location.reload(); // We reload to force clearing the username and password
                  }
                  else
                  {
                    this.performAfterLoginNavigation(user, userInfo, returnUrl, isRoleChanges);
                  }
                });
              }
              else
              {
                this.performAfterLoginNavigation(user, userInfo, returnUrl, isRoleChanges);
              }
            }
          });
        }
        else
        {
          this.performAfterLoginNavigation(user, userInfo, returnUrl, isRoleChanges);
        }
      }
    });
  }

  private processLoginUser(user: any, userInfo: any, changePassword: boolean): void 
  {
    user.name = userInfo.name;
    user.id = userInfo.id;
    user.role = userInfo.role;
    user.changePassword = changePassword;

    // Must clear fhir storage if exists
    this.fhirService.clearStorage();

    this.loadPermissions(user);
    this.saveUserToStorage(user);
    this.authenticationChange.next(user);
    this.webSocketService.connect(user.token);  // Connect WebSocket
  }

  private performAfterLoginNavigation(user: any, userInfo: any, returnUrl: string, isRoleChanges: boolean): void 
  {
    this.completingLogin = false;
    this.processLoginUser(user, userInfo, false);
    this.isLoggedInSubject.next(true);
    this.navigateToDefaultPage(returnUrl, isRoleChanges);
  }

  /**
   * Set whatever we use local or session storage for the user. For FHIR embedded mode, we always stored to the session storage.
   */
  private initTokenStorage(): void
  {
    this.cacheTokenToLocalStorage = this.appConfig.cacheTokenToLocalStorage && (!this.fhirService.embedded || this.fhirService.fullSite);
  }
}
